import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { SCORED_QUESTIONS, DIMENSIONS, ANSWERS, MATURITY_BANDS, DIM_SUPPORT } from "./govData";

const GovContext = createContext(null);
const KEY = "norse3_gov_state_v1";

const scoreOf = (v) => (ANSWERS.find((a) => a.value === v)?.score ?? 0);

function load() {
  try { const r = sessionStorage.getItem(KEY); if (r) return JSON.parse(r); } catch (e) { /* ignore */ }
  return { answers: {}, exposure: {}, policy: null, deploy: {}, qa: null, roi: null, framework: null, skjold: null };
}

export function GovProvider({ children }) {
  const [state, setState] = useState(load);

  useEffect(() => {
    try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* ignore */ }
  }, [state]);

  const setAnswer = useCallback((id, v) => setState((s) => ({ ...s, answers: { ...s.answers, [id]: v } })), []);
  const setExposure = useCallback((id, v) => setState((s) => ({ ...s, exposure: { ...s.exposure, [id]: v } })), []);
  const setPolicy = useCallback((p) => setState((s) => ({ ...s, policy: p })), []);
  const setDeploy = useCallback((d) => setState((s) => ({ ...s, deploy: d })), []);
  const setQa = useCallback((q) => setState((s) => ({ ...s, qa: q })), []);
  const setRoi = useCallback((r) => setState((s) => ({ ...s, roi: r })), []);
  const setFramework = useCallback((f) => setState((s) => ({ ...s, framework: f })), []);
  const setSkjold = useCallback((v) => setState((s) => ({ ...s, skjold: v })), []);
  const resetGov = useCallback(() => setState({ answers: {}, exposure: {}, policy: null, deploy: {}, qa: null, roi: null, framework: null, skjold: null }), []);

  const result = useMemo(() => {
    const answered = SCORED_QUESTIONS.filter((q) => state.answers[q.id]);
    if (answered.length === 0) return null;
    const dims = {};
    DIMENSIONS.forEach((d) => {
      const qs = SCORED_QUESTIONS.filter((q) => q.dim === d && state.answers[q.id]);
      dims[d] = qs.length ? Math.round(qs.reduce((a, q) => a + scoreOf(state.answers[q.id]), 0) / qs.length) : 0;
    });
    const overall = Math.round(Object.values(dims).reduce((a, b) => a + b, 0) / DIMENSIONS.length);
    const band = [...MATURITY_BANDS].reverse().find((b) => overall >= b.min) || MATURITY_BANDS[0];
    const sorted = [...DIMENSIONS].sort((a, b) => dims[b] - dims[a]);
    const strongest = sorted.filter((d) => dims[d] >= 60).slice(0, 3);
    const gaps = sorted.filter((d) => dims[d] < 60).reverse().slice(0, 3);
    return { dims, overall, band, strongest, gaps, complete: answered.length === SCORED_QUESTIONS.length, support: DIM_SUPPORT };
  }, [state.answers]);

  const value = { ...state, setAnswer, setExposure, setPolicy, setDeploy, setQa, setRoi, setFramework, setSkjold, resetGov, result };
  return <GovContext.Provider value={value}>{children}</GovContext.Provider>;
}

export function useGov() {
  const ctx = useContext(GovContext);
  if (!ctx) throw new Error("useGov must be used within GovProvider");
  return ctx;
}
